import * as THREE from 'three'

/**
 * 创建基础场景
 * @returns 场景对象
 */
export const createScene = () => {
  return new THREE.Scene()
}

/**
 * 创建透视相机
 * @param fov 视角
 * @param aspect 宽高比
 * @param near 近裁剪面
 * @param far 远裁剪面
 * @param position 相机位置
 * @returns 透视相机对象
 */
export const createPerspectiveCamera = (
  fov: number = 75,
  aspect: number = window.innerWidth / window.innerHeight,
  near: number = 0.1,
  far: number = 1000,
  position: THREE.Vector3 = new THREE.Vector3(0, 0, 5)
) => {
  const camera = new THREE.PerspectiveCamera(fov, aspect, near, far)
  camera.position.copy(position)
  return camera
}

/**
 * 创建正交相机
 * @param left 左边界
 * @param right 右边界
 * @param top 上边界
 * @param bottom 下边界
 * @param near 近裁剪面
 * @param far 远裁剪面
 * @param position 相机位置
 * @returns 正交相机对象
 */
export const createOrthographicCamera = (
  left: number = -1,
  right: number = 1,
  top: number = 1,
  bottom: number = -1,
  near: number = 0.1,
  far: number = 1000,
  position: THREE.Vector3 = new THREE.Vector3(0, 0, 5)
) => {
  const camera = new THREE.OrthographicCamera(left, right, top, bottom, near, far)
  camera.position.copy(position)
  return camera
}

/**
 * 创建WebGL渲染器
 * @param canvas 画布元素
 * @param options 渲染器选项
 * @returns WebGL渲染器对象
 */
export const createWebGLRenderer = (
  canvas?: HTMLCanvasElement,
  options: THREE.WebGLRendererParameters = {}
) => {
  const defaultOptions: THREE.WebGLRendererParameters = {
    antialias: true,
    alpha: true,
    ...options
  }
  
  const renderer = canvas 
    ? new THREE.WebGLRenderer({ canvas, ...defaultOptions })
    : new THREE.WebGLRenderer(defaultOptions)
  
  renderer.setSize(window.innerWidth, window.innerHeight)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  
  return renderer
}

/**
 * 添加环境光
 * @param scene 场景对象
 * @param color 光颜色
 * @param intensity 光强度
 * @returns 环境光对象
 */
export const addAmbientLight = (
  scene: THREE.Scene,
  color: THREE.ColorRepresentation = 0xffffff,
  intensity: number = 0.5
) => {
  const light = new THREE.AmbientLight(color, intensity)
  scene.add(light)
  return light
}

/**
 * 添加平行光
 * @param scene 场景对象
 * @param color 光颜色
 * @param intensity 光强度
 * @param position 光位置
 * @returns 平行光对象
 */
export const addDirectionalLight = (
  scene: THREE.Scene,
  color: THREE.ColorRepresentation = 0xffffff,
  intensity: number = 1,
  position: THREE.Vector3 = new THREE.Vector3(0, 1, 0)
) => {
  const light = new THREE.DirectionalLight(color, intensity)
  light.position.copy(position)
  scene.add(light)
  return light
}

/**
 * 添加点光源
 * @param scene 场景对象
 * @param color 光颜色
 * @param intensity 光强度
 * @param distance 光距离
 * @param position 光位置
 * @returns 点光源对象
 */
export const addPointLight = (
  scene: THREE.Scene,
  color: THREE.ColorRepresentation = 0xffffff,
  intensity: number = 1,
  distance: number = 0,
  position: THREE.Vector3 = new THREE.Vector3(0, 0, 0)
) => {
  const light = new THREE.PointLight(color, intensity, distance)
  light.position.copy(position)
  scene.add(light)
  return light
}

/**
 * 添加聚光灯
 * @param scene 场景对象
 * @param color 光颜色
 * @param intensity 光强度
 * @param distance 光距离
 * @param angle 光角度
 * @param penumbra 光半影
 * @param decay 光衰减
 * @param position 光位置
 * @returns 聚光灯对象
 */
export const addSpotLight = (
  scene: THREE.Scene,
  color: THREE.ColorRepresentation = 0xffffff,
  intensity: number = 1,
  distance: number = 0,
  angle: number = Math.PI / 4,
  penumbra: number = 0,
  decay: number = 1,
  position: THREE.Vector3 = new THREE.Vector3(0, 1, 0)
) => {
  const light = new THREE.SpotLight(color, intensity, distance, angle, penumbra, decay)
  light.position.copy(position)
  scene.add(light)
  return light
}

/**
 * 添加网格辅助线
 * @param scene 场景对象
 * @param size 网格大小
 * @param divisions 网格细分
 * @param colorGrid 网格颜色
 * @param colorCenterLine 中心线颜色
 * @returns 网格辅助线对象
 */
export const addGridHelper = (
  scene: THREE.Scene,
  size: number = 10,
  divisions: number = 10,
  colorGrid: THREE.ColorRepresentation = 0x888888,
  colorCenterLine: THREE.ColorRepresentation = 0xcccccc
) => {
  const gridHelper = new THREE.GridHelper(size, divisions, colorGrid, colorCenterLine)
  scene.add(gridHelper)
  return gridHelper
}

/**
 * 添加坐标轴辅助线
 * @param scene 场景对象
 * @param size 辅助线大小
 * @returns 坐标轴辅助线对象
 */
export const addAxesHelper = (
  scene: THREE.Scene,
  size: number = 5
) => {
  const axesHelper = new THREE.AxesHelper(size)
  scene.add(axesHelper)
  return axesHelper
}

/**
 * 渲染场景
 * @param scene 场景对象
 * @param camera 相机对象
 * @param renderer 渲染器对象
 */
export const renderScene = (
  scene: THREE.Scene,
  camera: THREE.Camera,
  renderer: THREE.WebGLRenderer
) => {
  renderer.render(scene, camera)
}

/**
 * 处理窗口大小变化
 * @param camera 相机对象
 * @param renderer 渲染器对象
 */
export const handleWindowResize = (
  camera: THREE.Camera,
  renderer: THREE.WebGLRenderer
) => {
  if (camera instanceof THREE.PerspectiveCamera) {
    camera.aspect = window.innerWidth / window.innerHeight
    camera.updateProjectionMatrix()
  } else if (camera instanceof THREE.OrthographicCamera) {
    const width = window.innerWidth
    const height = window.innerHeight
    camera.left = -width / 2
    camera.right = width / 2
    camera.top = height / 2
    camera.bottom = -height / 2
    camera.updateProjectionMatrix()
  }
  
  renderer.setSize(window.innerWidth, window.innerHeight)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
}

/**
 * 清除场景中的所有对象
 * @param scene 场景对象
 */
export const clearScene = (scene: THREE.Scene) => {
  while (scene.children.length > 0) {
    const child = scene.children[0]
    scene.remove(child)
    
    if (child instanceof THREE.Mesh) {
      child.geometry.dispose()
      if (child.material instanceof THREE.Material) {
        child.material.dispose()
      } else if (Array.isArray(child.material)) {
        child.material.forEach(material => material.dispose())
      }
    }
  }
}